/**
 * PRE-DISPATCH CHECKLIST SCREEN
 *
 * A short, gated 6-step flow the driver must clear before the backend will let
 * them mark a job EN_ROUTE_TO_SELLER. Mirrors the approved demo
 * (/demo/predispatch-checklist) built natively in RN — NOT a webview.
 *
 *   Step 1  Brief    — job brief: gallery (listing + pre-pickup photos) + lightbox,
 *                      item/route/specs, heads-up flags, install line, walkthrough
 *                      and path videos; close with one tap-to-confirm checkbox.
 *   Step 2  Order    — 6 tappable "order of operations" rows (all required).
 *   Step 3  Rules    — 5 tappable rule cards (all required).
 *   Step 4  Gear     — collapsible Helpful Hints lightbulb card, then equipment
 *                      items (yes/no, photo, confirm, or confirm+photo).
 *   Step 5  Vehicle  — empty cargo photo (required), fuel yes/no, fit-to-drive yes/no.
 *   Step 6  Confirm  — "Start route". Any required No/missing → blocking
 *                      "Cannot start as equipped" sheet → "Notify the team".
 *
 * Everything order-specific (brief, equipment, guide, rules, ops, media) comes
 * from GET /api/jobs/:id/predispatch. Submission is multipart to
 * POST /api/jobs/:id/predispatch/submit. The whole screen is reachable only when
 * PREDISPATCH_ENABLED is true (callers gate navigation + the home popup).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity,
  Alert, ActivityIndicator, Platform, Image, Modal, Dimensions,
  Animated, Linking, NativeSyntheticEvent, NativeScrollEvent,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { preparePhoto } from '../../utils/webPhoto';
import { WalkInCard } from '../../components/LightbulbBody';
import { markJobBriefViewed } from '../../services/jobBrief';
import ImageLightbox from '../../components/ImageLightbox';

const SCREEN = Dimensions.get('window');

// ── API types (mirror the predispatch contract) ────────────────────────────
interface MediaItem { type?: string; url: string; poster?: string }
interface EquipmentItem {
  key: string;
  label: string;
  sub?: string;
  spec?: string;
  input: 'yesno' | 'photo' | 'confirm' | 'confirm_photo';
  required: boolean;
}
interface RuleItem { t: string; d?: string }
interface PreDispatchData {
  job: {
    id: string;
    title?: string;
    categorySlug?: string;
    cat?: string;
    photos?: string[];
    inspectionPhotos?: string[];
    dims?: string;
    weight?: string;
    weightClass?: string;
    flags?: string[];
    pickup?: { floor?: string | number; elevator?: boolean; stairs?: string | number; parking?: string };
    delivery?: { floor?: string | number; elevator?: boolean; stairs?: string | number; parking?: string };
    helpers?: number;
    needsInstall?: boolean;
  };
  media?: { walkthrough?: MediaItem[]; sellerPath?: MediaItem[]; buyerPath?: MediaItem[] };
  guide?: { specs?: string[]; precautions?: string[]; steps?: string[]; videos?: string[] };
  equipment?: EquipmentItem[];
  rules?: RuleItem[];
  orderOfOps?: string[];
  status?: 'PENDING' | 'PASSED' | 'BLOCKED';
  completedAt?: string | null;
}

const STEPS = ['Brief', 'Order', 'Rules', 'Gear', 'Vehicle', 'Confirm'] as const;

// Hot-tub / swim-spa items skip the install/setup language (crew handles those
// differently). Mirrors categoryMapping's hot-tub detection.
function isHotTubLike(d: PreDispatchData | null): boolean {
  if (!d) return false;
  const hay = `${d.job?.categorySlug || ''} ${d.job?.cat || ''} ${d.job?.title || ''}`.toLowerCase();
  return hay.includes('hot tub') || hay.includes('hot-tub') || hay.includes('hottub')
    || hay.includes('swim spa') || hay.includes('swim-spa');
}

function toArr(v: string[] | string | null | undefined): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.filter(Boolean);
  try { const p = JSON.parse(v); return Array.isArray(p) ? p.filter(Boolean) : []; } catch { return []; }
}

export function PreDispatchChecklistScreen({ route, navigation }: any) {
  const { jobId, job: passedJob, onDone } = route.params || {};

  const [data, setData] = useState<PreDispatchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [step, setStep] = useState(0); // 0..5
  const [scrollPct, setScrollPct] = useState(0);

  // Answers, keyed per concern.
  const [briefConfirmed, setBriefConfirmed] = useState(false);
  const [opsChecks, setOpsChecks] = useState<Record<number, boolean>>({});
  const [ruleChecks, setRuleChecks] = useState<Record<number, boolean>>({});
  // equipment: per key → 'yes' | 'no' | 'confirmed' | undefined
  const [equipAnswers, setEquipAnswers] = useState<Record<string, string>>({});
  const [equipPhotos, setEquipPhotos] = useState<Record<string, string>>({}); // key → uri
  // vehicle & self
  const [cargoPhoto, setCargoPhoto] = useState<string | null>(null);
  const [fuelOk, setFuelOk] = useState<string | undefined>();
  const [fitToDrive, setFitToDrive] = useState<string | undefined>();

  // UI
  const [lightbox, setLightbox] = useState<{ list: string[]; index: number } | null>(null);
  const [hintsOpen, setHintsOpen] = useState(false);
  const [gearGuideOpen, setGearGuideOpen] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);
  /**
   * 🚨 THE ESCAPE HATCH WAS ONLY AT THE END, AND YOU HAVE TO GET THERE TO USE IT.
   *
   * `onStartRoutePress` says "the driver is never blocked" and means it - on the LAST step.
   * Every step before it hard-disables Continue, so a driver who cannot satisfy step 4 can
   * never reach step 6, where the sheet that tells the team about it lives.
   *
   * Shonrick Mcdaniel, 2026-09-10, on his third attempt after a reinstall and a cache
   * clear: "It's still stuck on gear equipment. It won't let me continue. I can't do this
   * job until confirmed on app. If you have someone else to do it, fine with me." His job
   * wants three separate photos. We checked the data, the gate, the rendered controls and
   * the shipped bundle, and all four are correct - which is the point. A gate that is
   * satisfiable in principle and not by THIS driver on THIS phone still ends with a real
   * job stalled and a driver offering to give it up.
   *
   * What he could not do is recorded per step and carried to the final gate, which already
   * knows how to submit a flagged check-in and text the team. The requirement does not
   * soften: the run is flagged either way. The driver just stops being trapped.
   */
  const [skipped, setSkipped] = useState<Record<number, string[]>>({});
  const [submitting, setSubmitting] = useState(false);
  const [blockedReason, setBlockedReason] = useState<string | null>(null);

  const bulbPulse = useRef(new Animated.Value(1)).current;
  const scrollRef = useRef<ScrollView>(null);
  // Seeded at the old constant so the first frame is never worse than before.
  const [barHeight, setBarHeight] = useState(140);

  // ── Persist progress across reloads (iOS PWA camera fix) ───────────────────
  // On iOS the web app can be evicted/reloaded when the camera opens, which would
  // otherwise drop the driver back to step 0 with everything blank ("app starts
  // over when taking pictures"). We snapshot progress to storage on every change
  // and restore it on mount, so the driver keeps their place + answers. Purely
  // additive — cleared once the checklist is submitted.
  const PERSIST_KEY = `cp_predispatch_progress_${jobId || passedJob?.id || ''}`;
  const restoreAttempted = useRef(false);
  const [restored, setRestored] = useState(false);
  const clearPersisted = () => { AsyncStorage.removeItem(PERSIST_KEY).catch(() => {}); };

  useEffect(() => {
    const id = jobId || passedJob?.id;
    if (!id || restoreAttempted.current) return;
    restoreAttempted.current = true;
    (async () => {
      try {
        const raw = await AsyncStorage.getItem(PERSIST_KEY);
        if (raw) {
          const s = JSON.parse(raw);
          if (typeof s.step === 'number') setStep(s.step);
          if (typeof s.briefConfirmed === 'boolean') setBriefConfirmed(s.briefConfirmed);
          if (s.opsChecks) setOpsChecks(s.opsChecks);
          if (s.ruleChecks) setRuleChecks(s.ruleChecks);
          if (s.equipAnswers) setEquipAnswers(s.equipAnswers);
          if (s.equipPhotos) setEquipPhotos(s.equipPhotos);
          if (s.cargoPhoto) setCargoPhoto(s.cargoPhoto);
          if (s.fuelOk) setFuelOk(s.fuelOk);
          if (s.fitToDrive) setFitToDrive(s.fitToDrive);
          if (s.skipped) setSkipped(s.skipped);
        }
      } catch {}
      setRestored(true); // only now may the persist effect write (avoids clobbering)
    })();
  }, [jobId, passedJob?.id]);

  useEffect(() => {
    const id = jobId || passedJob?.id;
    if (!id || !restored) return;
    // Persist progress, tiered under the ~5MB localStorage quota: try the full snapshot
    // (incl. base64 photos), but if it's too big fall back to answers-only so a photo-heavy
    // flow can't silently overflow the quota and stop persisting EVERYTHING (incl. the tiny
    // step/answer fields). Without this, enough photos = total progress loss on reload.
    const base = { step, briefConfirmed, opsChecks, ruleChecks, equipAnswers, fuelOk, fitToDrive, skipped, savedAt: Date.now() };
    /**
     * 🚨 DROP THE BIGGEST PHOTO, NOT ALL OF THEM. The old fallback was
     * `equipPhotos: {}, cargoPhoto: null` the moment the snapshot went over the tier, so
     * one oversized photo threw away every photo the driver had already taken - on a step
     * that wants three of them. Combined with the iOS-PWA reload, that is a driver who can
     * never hold two photos at once no matter how many times he tries.
     *
     * Now they come off one at a time, largest first, so as much progress survives as will
     * fit. With `shrinkForWeb` above keeping each around 250KB this should never fire at
     * all; it is the backstop for a device whose canvas would not decode the photo.
     */
    const photos: Record<string, string> = { ...equipPhotos };
    let cargo: string | null = cargoPhoto;
    const size = () => JSON.stringify({ ...base, equipPhotos: photos, cargoPhoto: cargo }).length;
    let guard = Object.keys(photos).length + 2;
    while (size() > 4_500_000 && guard-- > 0) {
      const keys = Object.keys(photos);
      const biggestKey = keys.sort((a, b) => (photos[b]?.length ?? 0) - (photos[a]?.length ?? 0))[0];
      const biggestLen = biggestKey ? photos[biggestKey].length : 0;
      if (cargo && cargo.length >= biggestLen) { cargo = null; continue; }
      if (!biggestKey) break;
      delete photos[biggestKey];
    }
    AsyncStorage.setItem(PERSIST_KEY, JSON.stringify({ ...base, equipPhotos: photos, cargoPhoto: cargo })).catch(() => {});
  }, [restored, step, briefConfirmed, opsChecks, ruleChecks, equipAnswers, equipPhotos, cargoPhoto, fuelOk, fitToDrive, skipped]);

  useEffect(() => {
    let cancelled = false;
    const id = jobId || passedJob?.id;
    if (!id) { setLoading(false); setLoadError(true); return; }
    api.get(`/jobs/${id}/predispatch`)
      .then(({ data: d }) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setLoadError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, [jobId, passedJob?.id]);

  // Pulse the lightbulb FAB.
  useEffect(() => {
    const anim = Animated.loop(
      Animated.sequence([
        Animated.timing(bulbPulse, { toValue: 0.35, duration: 650, useNativeDriver: true }),
        Animated.timing(bulbPulse, { toValue: 1, duration: 650, useNativeDriver: true }),
      ]),
    );
    anim.start();
    return () => anim.stop();
  }, [bulbPulse]);

  // Reset scroll position + progress whenever the step changes.
  useEffect(() => {
    setScrollPct(0);
    scrollRef.current?.scrollTo({ y: 0, animated: false });
  }, [step]);

  const isHotTub = useMemo(() => isHotTubLike(data), [data]);
  const showInstall = !!data?.job?.needsInstall && !isHotTub;

  // ── Gallery: listing photos + pre-pickup inspection photos in ONE list ────
  const gallery = useMemo(() => {
    const listing = toArr(data?.job?.photos);
    const inspection = toArr(data?.job?.inspectionPhotos);
    return [...listing, ...inspection];
  }, [data]);

  // ── Order of operations (6) — from API, with an install step woven in for
  // non hot-tub/swim-spa items when none was provided. ──────────────────────
  const orderOps = useMemo(() => {
    const ops = (data?.orderOfOps || []).filter(Boolean);
    if (ops.length >= 6) return ops.slice(0, 6);
    // Backfill toward 6 so the demo's "6 rows" shape always holds.
    const base = ops.length > 0 ? [...ops] : [
      'Inspect and photograph the item with the seller before moving anything',
      'Confirm the item matches the listing and complete the inspection',
      'Protect, pad, and secure the item for transport',
      'Load safely with your crew and strap it down',
      'Deliver, then place the item exactly where the buyer wants it',
    ];
    const hasInstall = base.some(o => /install|set ?up|assemble/i.test(o));
    if (showInstall && !hasInstall) {
      base.push('Install and set up the item, then confirm it works with the buyer');
    } else if (base.length < 6) {
      base.push('Complete the buyer checklist and get the buyer signature');
    }
    while (base.length < 6) base.push('Leave the home clean with no damage');
    return base.slice(0, 6);
  }, [data, showInstall]);

  const rules = useMemo<RuleItem[]>(() => {
    const r = (data?.rules || []).filter(x => x && x.t);
    if (r.length >= 5) return r.slice(0, 5);
    const base = r.length > 0 ? [...r] : [
      { t: 'Inspection first, always', d: 'Photos and the checklist come before you move or load anything.' },
      { t: 'Bring your own crew and gear', d: 'Show up with the helpers, straps, and equipment this job needs.' },
      { t: 'Communicate clearly', d: 'Keep the seller, buyer, and team updated through the app.' },
      { t: 'No damage', d: 'Pad, protect, and leave both homes exactly as you found them.' },
    ];
    if (!base.some(x => /clean|presentable/i.test(x.t))) {
      base.push({ t: 'Show up clean and presentable', d: 'You represent Commonplace at every door.' });
    }
    while (base.length < 5) base.push({ t: 'Follow every step in order', d: 'Do the in-app steps exactly, top to bottom.' });
    // Guarantee the 5th rule is the "clean and presentable" one per the demo.
    const cleanIdx = base.findIndex(x => /clean|presentable/i.test(x.t));
    if (cleanIdx > -1 && cleanIdx !== 4) {
      const [clean] = base.splice(cleanIdx, 1);
      base.splice(4, 0, clean);
    }
    return base.slice(0, 5);
  }, [data]);

  const equipment = useMemo<EquipmentItem[]>(() => (data?.equipment || []).filter(e => e && e.key), [data]);

  // ── Completion logic per step ─────────────────────────────────────────────
  const opsComplete = orderOps.every((_, i) => opsChecks[i]);
  const rulesComplete = rules.every((_, i) => ruleChecks[i]);

  const equipDone = (e: EquipmentItem): boolean => {
    if (e.input === 'photo') return !!equipPhotos[e.key];
    if (e.input === 'confirm') return equipAnswers[e.key] === 'confirmed';
    if (e.input === 'confirm_photo') return equipAnswers[e.key] === 'confirmed' && !!equipPhotos[e.key];
    // yesno
    return equipAnswers[e.key] === 'yes' || equipAnswers[e.key] === 'no';
  };
  // Required gear is satisfied only when answered AND not "No"/missing.
  const equipRequiredOk = (e: EquipmentItem): boolean => {
    if (!e.required) return true;
    if (e.input === 'yesno') return equipAnswers[e.key] === 'yes';
    return equipDone(e);
  };
  const gearAnswered = equipment.every(equipDone);
  const gearRequiredOk = equipment.every(equipRequiredOk);
  // Step gate: only REQUIRED gear must be ANSWERED to advance — an item badged "Optional"
  // must never block Continue (that was a real "can't proceed" trap). A "No" on a required
  // item still advances here and is caught at the final PASS/BLOCKED gate.
  const gearStepAnswered = equipment.every(e => !e.required || equipDone(e));

  const vehicleAnswered = !!cargoPhoto && (fuelOk === 'yes' || fuelOk === 'no') && (fitToDrive === 'yes' || fitToDrive === 'no');
  const vehicleRequiredOk = !!cargoPhoto && fuelOk === 'yes' && fitToDrive === 'yes';

  /**
   * 🚨 A GREY BUTTON IS NOT AN ERROR MESSAGE.
   *
   * Continue is hard-disabled until the step's gate passes, and nothing told the driver
   * WHICH of six items was incomplete. Shonrick Mcdaniel, 2026-09-10: "It got stuck on #4.
   * Ex. Confirm equipment, helper etc. The continue would not turn green. Preventing me
   * from continue." He was standing in front of a Peloton Tread whose gear step needs
   * three separate photos (helpers, blankets, dolly) plus three yes/no answers, with no
   * way to see which one he had missed.
   *
   * Says the exact items, and for each one what it still wants.
   */
  const missingForStep = (sIdx: number): string[] => {
    switch (sIdx) {
      case 0: return briefConfirmed ? [] : ['Confirm you have read the brief'];
      case 1: return orderOps.filter((_, i) => !opsChecks[i]).map(l => String(l));
      case 2: return rules.filter((_, i) => !ruleChecks[i]).map(r => r.t);
      case 3:
        return equipment
          .filter(e => e.required && !equipDone(e))
          .map(e => {
            const needConfirm = (e.input === 'confirm' || e.input === 'confirm_photo') && equipAnswers[e.key] !== 'confirmed';
            const needPhoto = (e.input === 'photo' || e.input === 'confirm_photo') && !equipPhotos[e.key];
            const needYesno = e.input === 'yesno' && equipAnswers[e.key] !== 'yes' && equipAnswers[e.key] !== 'no';
            const wants = [needConfirm && 'tick "I have this"', needPhoto && 'add a photo', needYesno && 'answer Yes or No']
              .filter(Boolean).join(' and ');
            return wants ? `${e.label} - ${wants}` : e.label;
          });
      case 4: {
        const out: string[] = [];
        if (!cargoPhoto) out.push('Cargo area - add a photo');
        if (fuelOk !== 'yes' && fuelOk !== 'no') out.push('Enough fuel - answer Yes or No');
        if (fitToDrive !== 'yes' && fitToDrive !== 'no') out.push('Fit to drive - answer Yes or No');
        return out;
      }
      default: return [];
    }
  };

  // Whether the current step's gate is satisfied enough to advance.
  const stepReady = (s: number): boolean => {
    switch (s) {
      case 0: return briefConfirmed;
      case 1: return opsComplete;
      case 2: return rulesComplete;
      case 3: return gearStepAnswered; // required gear answered; optional never blocks; "No" handled at final gate
      case 4: return vehicleAnswered;  // must answer all; "No" handled at final gate
      case 5: return true;
      default: return false;
    }
  };

  // Whether everything REQUIRED passed (drives PASS vs BLOCKED at submit).
  // A step the driver could not complete is never a pass, whatever the individual
  // answers happen to say - that is the whole point of letting them through it.
  const skippedAnything = Object.values(skipped).some(v => v && v.length > 0);
  const allRequiredPassed = briefConfirmed && opsComplete && rulesComplete
    && gearRequiredOk && vehicleRequiredOk && !skippedAnything;

  const firstBlockingReason = (): string | null => {
    // What the driver actually reported he could not do comes first: it is the reason,
    // and it is more specific than re-deriving one from the empty answers it left behind.
    const first = Object.keys(skipped).map(Number).sort((a, b) => a - b)
      .find(i => (skipped[i] || []).length > 0);
    if (first != null) return `Driver could not complete ${STEPS[first]}: ${skipped[first].join('; ')}`;
    if (!gearRequiredOk) {
      const e = equipment.find(x => !equipRequiredOk(x));
      if (e) return `Missing required gear: ${e.label}`;
    }
    if (!cargoPhoto) return 'Empty cargo photo is required';
    if (fuelOk === 'no') return 'Not enough fuel for the route';
    if (fitToDrive === 'no') return 'Driver reported not fit to drive';
    return null;
  };

  const goNext = () => {
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const goBack = () => {
    if (lightbox) { setLightbox(null); return; }
    if (hintsOpen) { setHintsOpen(false); return; }
    if (showSupport) { setShowSupport(false); return; }
    if (showBlocked) { setShowBlocked(false); return; }
    if (step > 0) { setStep(step - 1); return; }
    navigation.goBack();
  };

  // ── Add a photo (camera OR library) ────────────────────────────────────────
  // Use the photo-library picker: on mobile web / iOS it presents BOTH "Take Photo"
  // and "Choose from Library", and it's far more reliable than the camera-only capture
  // (launchCameraAsync forces a flaky capture path on the web app, which left drivers
  // unable to add pictures). Falls back to the camera if the library picker fails.
  // Convert a picked web URI to a self-contained data: URI so it survives an iOS-PWA
  // reload on return from the picker (blob: URIs die on reload — which broke submit when
  // a persisted blob: was later fetched). data: also uploads cleanly via the branch below.
  // toDataUri + shrinkForWeb live in utils/webPhoto: four screens had their own copy and
  // one of them had never run. See the note at the top of that file for why a picked photo
  // has to be both reload-proof and small.

  const capturePhoto = async (onUri: (uri: string) => void) => {
    const store = async (uri: string) => onUri(await preparePhoto(uri));
    let libError: unknown = null;
    try {
      const lib = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.8 });
      if (!lib.canceled && lib.assets?.[0]) { await store(lib.assets[0].uri); return; }
      if (lib.canceled) return; // driver dismissed the picker on purpose
    } catch (e) { libError = e; }
    try {
      const cam = await ImagePicker.launchCameraAsync({ quality: 0.8 });
      if (!cam.canceled && cam.assets?.[0]) { await store(cam.assets[0].uri); return; }
      if (cam.canceled) return;
      // Neither branch produced a file and neither threw: say so rather than leaving the
      // driver staring at a button that did nothing.
      Alert.alert('Photo not added', 'That photo did not come through. Please try again, or use "Notify team" at the end if it keeps failing.');
    } catch {
      Alert.alert(
        'Could not add photo',
        libError
          ? 'We could not open your photos or your camera. Please allow photo/camera access for this site in your browser settings, then try again.'
          : 'Please allow photo/camera access for this site in your browser settings, then try again.',
      );
    }
  };

  // ── Submit (multipart) ────────────────────────────────────────────────────
  // The FULL response shape — the exact answers the driver gave. Used by BOTH the
  // normal "Start route" submit AND the "notify team & continue" flagged submit,
  // so the backend always evaluates the driver's REAL answers and the flag reason
  // reflects what THIS driver actually left undone (not a generic blanket flag).
  const buildResponses = () => ({
    brief: { confirmed: briefConfirmed },
    orderOfOps: orderOps.map((label, i) => ({ label, checked: !!opsChecks[i] })),
    rules: rules.map((r, i) => ({ t: r.t, checked: !!ruleChecks[i] })),
    equipment: equipment.map(e => ({
      key: e.key,
      label: e.label,
      input: e.input,
      required: e.required,
      answer: equipAnswers[e.key] ?? null,
      hasPhoto: !!equipPhotos[e.key],
    })),
    vehicle: { cargoPhoto: !!cargoPhoto, fuelOk: fuelOk ?? null, fitToDrive: fitToDrive ?? null },
    passedClientSide: allRequiredPassed,
  });

  const appendPhotoFiles = async (formData: FormData) => {
    const files: { field: string; uri: string }[] = [];
    if (cargoPhoto) files.push({ field: 'cargo', uri: cargoPhoto });
    for (const key of Object.keys(equipPhotos)) {
      if (equipPhotos[key]) files.push({ field: key, uri: equipPhotos[key] });
    }
    for (let i = 0; i < files.length; i++) {
      const { field, uri } = files[i];
      const fileName = `predispatch-${field}-${Date.now()}-${i}.jpg`;
      // Per-file guard: a single dead blob: URI (after a reload) must NOT abort the whole
      // submit — skip it so the driver can still complete the checklist.
      try {
        if (Platform.OS === 'web' && (uri.startsWith('data:') || uri.startsWith('blob:'))) {
          const resp = await fetch(uri); const blob = await resp.blob();
          formData.append(field, blob, fileName);
        } else {
          formData.append(field, { uri, type: 'image/jpeg', name: fileName } as any);
        }
      } catch (e: any) {
        console.warn(`predispatch photo "${field}" skipped (unreadable uri):`, e?.message || e);
      }
    }
  };

  const handleStartRoute = async () => {
    const id = jobId || passedJob?.id;
    if (!id) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('responses', JSON.stringify(buildResponses()));
      await appendPhotoFiles(formData);
      await api.post(`/jobs/${id}/predispatch/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      setSubmitting(false);
      // Whether it PASSED or got flagged, the check-in is SUBMITTED — the driver is
      // cleared to head out. A flag just alerts the team; it never stops the driver.
      finishPassed();
    } catch (e: any) {
      setSubmitting(false);
      Alert.alert('Could not submit', e?.response?.data?.error || 'Please check your connection and try again.');
    }
  };

  const finishPassed = () => {
    clearPersisted(); // checklist submitted — drop the saved in-progress snapshot
    if (typeof onDone === 'function') { onDone('PASSED'); return; }
    // Default: route into the active job so the driver can start the route.
    navigation.navigate('ActiveJob', {
      jobId: jobId || passedJob?.id,
      job: passedJob,
      predispatchPassed: true,
    });
  };

  // Final "Start route" press: if anything required is No/missing, open the
  // heads-up sheet (we'll flag it to the team) — but the driver is never blocked.
  // Otherwise submit normally.
  const onStartRoutePress = () => {
    if (!allRequiredPassed) {
      setBlockedReason(firstBlockingReason());
      setShowBlocked(true);
      return;
    }
    handleStartRoute();
  };

  // From the heads-up sheet: submit the check-in (flagged) so the backend texts
  // the team, then let the driver continue to the route. We never stop them.
  const notifyAndContinue = async () => {
    const id = jobId || passedJob?.id;
    setSubmitting(true);
    try {
      // Send the SAME full answers as a normal submit (plus a blocked hint), so the
      // backend flags THIS driver's actual missing items — not a generic blanket.
      const formData = new FormData();
      formData.append('responses', JSON.stringify({ ...buildResponses(), blocked: true, reason: blockedReason || firstBlockingReason(), skipped }));
      await appendPhotoFiles(formData);
      if (id) await api.post(`/jobs/${id}/predispatch/submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    } catch {
      // Even if the network call fails, proceed — the backend also flags from the
      // en-route path, and we never want to strand the driver.
    }
    setSubmitting(false);
    setShowBlocked(false);
    finishPassed();
  };

  // ── Render guards ─────────────────────────────────────────────────────────
  if (loading) {
    return <View style={s.loadingWrap}><ActivityIndicator size="large" color={RD.ink} /></View>;
  }
  if (loadError || !data) {
    return (
      <View style={s.loadingWrap}>
        <Ionicons name="cloud-offline-outline" size={40} color={RD.g30} />
        <Text style={s.errTitle}>Could not load the pre-dispatch checklist</Text>
        <TouchableOpacity style={s.errBtn} onPress={() => navigation.goBack()} activeOpacity={0.85}>
          <Text style={s.errBtnText}>Go back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const guide = data.guide || {};
  const media = data.media || {};
  const walkthrough = (media.walkthrough || []).filter(m => m && m.url);
  const sellerPath = (media.sellerPath || []).filter(m => m && m.url);
  const buyerPath = (media.buyerPath || []).filter(m => m && m.url);
  const hasWalkOrPath = walkthrough.length > 0 || sellerPath.length > 0 || buyerPath.length > 0;

  const headsUp = (data.job?.flags || []).filter(Boolean);

  const onScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
    const scrollable = Math.max(1, contentSize.height - layoutMeasurement.height);
    setScrollPct(Math.min(1, Math.max(0, contentOffset.y / scrollable)));
  };

  // ── Header / stepper ──────────────────────────────────────────────────────
  const Stepper = (
    <View style={s.stepperWrap}>
      {STEPS.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <View key={label} style={s.stepperCol}>
            <View style={[
              s.stepperSeg,
              done && s.stepperSegDone,
              active && s.stepperSegActive,
            ]} />
            <Text style={[s.stepperLabel, active && s.stepperLabelActive, done && s.stepperLabelDone]} numberOfLines={1}>
              {label}
            </Text>
          </View>
        );
      })}
    </View>
  );

  return (
    <View style={s.container}>
      {/* Header — back chevron goes back a step */}
      <View style={s.header}>
        <TouchableOpacity onPress={goBack} style={s.backBtn} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }} activeOpacity={0.7}>
          <Ionicons name="chevron-back" size={24} color={RD.text} />
        </TouchableOpacity>
        <View style={s.headerCenter}>
          <Text style={s.headerTitle}>Pre-dispatch check</Text>
          <Text style={s.headerSub}>Step {step + 1} of {STEPS.length} · {STEPS[step]}</Text>
        </View>
        <View style={{ width: 22 }} />
      </View>

      {Stepper}

      {/* Scroll progress bar (RD.money) */}
      <View style={s.progressBar}>
        <View style={[s.progressFill, { width: `${Math.round(scrollPct * 100)}%` }]} />
      </View>

      <ScrollView
        ref={scrollRef}
        contentContainerStyle={[s.scroll, { paddingBottom: barHeight + 28 }]}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={onScroll}
      >
        {/* ───────────────────────── STEP 1 — BRIEF ───────────────────────── */}
        {step === 0 && (
          <>
            <SectionTitle icon="reader-outline" color={RD.ink} text="Job brief" />

            {/* Item + route */}
            <View style={s.card}>
              <Text style={s.briefItem}>{data.job?.title || 'Item'}</Text>
              <View style={s.briefMetaRow}>
                {!!data.job?.cat && <View style={s.catPill}><Text style={s.catPillText}>{data.job.cat}</Text></View>}
                {!!data.job?.weightClass && (
                  <View style={s.classPill}><Text style={s.classPillText}>{data.job.weightClass}</Text></View>
                )}
                {typeof data.job?.helpers === 'number' && data.job.helpers > 0 && (
                  <View style={s.crewPill}>
                    <Ionicons name="people-outline" size={12} color={RD.textSec} />
                    <Text style={s.crewPillText}>{`1 driver + ${data.job.helpers} helper${data.job.helpers > 1 ? 's' : ''}`}</Text>
                  </View>
                )}
              </View>
              {(!!data.job?.dims || !!data.job?.weight) && (
                <View style={s.specRow}>
                  {!!data.job?.dims && <SpecChip icon="resize-outline" label={data.job.dims} />}
                  {!!data.job?.weight && <SpecChip icon="barbell-outline" label={data.job.weight} />}
                </View>
              )}
            </View>

            {/* Gallery — listing + pre-pickup photos in ONE row, opens lightbox */}
            {gallery.length > 0 && (
              <View style={s.card}>
                <Text style={s.cardLabel}>Photos — tap to enlarge and swipe</Text>
                <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -4 }}>
                  {gallery.map((uri, i) => (
                    <TouchableOpacity key={i} onPress={() => setLightbox({ list: gallery, index: i })} activeOpacity={0.85}>
                      <Image source={{ uri }} style={s.galleryThumb} resizeMode="cover" />
                      <View style={s.galleryZoomHint}><Ionicons name="expand-outline" size={12} color="#fff" /></View>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}

            {/* Heads up — amber flags list (not pills) */}
            {headsUp.length > 0 && (
              <View style={s.headsUpCard}>
                <View style={s.headsUpHead}>
                  <Ionicons name="alert-circle" size={16} color={RD.amber} />
                  <Text style={s.headsUpTitle}>Heads up</Text>
                </View>
                {headsUp.map((f, i) => (
                  <View key={i} style={[s.headsUpRow, i > 0 && { marginTop: 8 }]}>
                    <Ionicons name="ellipse" size={6} color={RD.amber} style={{ marginTop: 6 }} />
                    <Text style={s.headsUpText}>{f}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Install & setup line — non hot-tub/swim-spa only */}
            {showInstall && (
              <View style={s.installLine}>
                <Ionicons name="construct-outline" size={16} color={RD.info} />
                <Text style={s.installText}>Install and setup is included on this job. Plan to assemble and confirm it works with the buyer.</Text>
              </View>
            )}

            {/* Walkthrough and path — videos open to play */}
            {hasWalkOrPath && (
              <View style={s.card}>
                <Text style={s.cardLabel}>Walkthrough and path</Text>
                {walkthrough.map((m, i) => (
                  <VideoRow key={`w${i}`} accent={RD.maroon} label={`Seller walkthrough${walkthrough.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />
                ))}
                {sellerPath.map((m, i) => (
                  <VideoRow key={`s${i}`} accent={RD.maroon} label={`Seller path${sellerPath.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />
                ))}
                {buyerPath.map((m, i) => (
                  <VideoRow key={`b${i}`} accent={RD.info} label={`Buyer path${buyerPath.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />
                ))}
              </View>
            )}

            {/* Confirm checkbox to close the brief */}
            <TapConfirm
              checked={briefConfirmed}
              onToggle={() => setBriefConfirmed(v => !v)}
              label="I have reviewed the brief, photos, and path"
            />
          </>
        )}

        {/* ─────────────────────── STEP 2 — ORDER OF OPS ──────────────────── */}
        {step === 1 && (
          <>
            <SectionTitle icon="list-outline" color={RD.ink} text="Order of operations" />
            <Text style={s.stepHint}>Tap each step to confirm you understand the sequence. All are required.</Text>
            <View style={s.card}>
              {orderOps.map((op, i) => {
                const on = !!opsChecks[i];
                return (
                  <TouchableOpacity
                    key={i}
                    style={[s.opRow, i > 0 && s.rowBorder]}
                    onPress={() => setOpsChecks(p => ({ ...p, [i]: !p[i] }))}
                    activeOpacity={0.7}
                  >
                    <View style={[s.checkCircle, on && s.checkCircleOn]}>
                      {on ? <Ionicons name="checkmark" size={15} color="#fff" /> : <Text style={s.opNum}>{i + 1}</Text>}
                    </View>
                    <Text style={s.opText}>{op}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </>
        )}

        {/* ─────────────────────────── STEP 3 — RULES ─────────────────────── */}
        {step === 2 && (
          <>
            <SectionTitle icon="shield-checkmark-outline" color={RD.ink} text="The rules" />
            <Text style={s.stepHint}>Tap each card to agree. All 5 are required.</Text>
            {rules.map((r, i) => {
              const on = !!ruleChecks[i];
              return (
                <TouchableOpacity
                  key={i}
                  style={[s.ruleCard, on && s.ruleCardOn]}
                  onPress={() => setRuleChecks(p => ({ ...p, [i]: !p[i] }))}
                  activeOpacity={0.8}
                >
                  <View style={[s.checkBox, on && s.checkBoxOn]}>
                    {on && <Ionicons name="checkmark" size={15} color="#fff" />}
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={s.ruleTitle}>{r.t}</Text>
                    {!!r.d && <Text style={s.ruleDesc}>{r.d}</Text>}
                  </View>
                  <View style={s.requiredBadge}><Text style={s.requiredText}>Required</Text></View>
                </TouchableOpacity>
              );
            })}
          </>
        )}

        {/* ─────────────────── STEP 4 — EQUIPMENT & CREW ──────────────────── */}
        {step === 3 && (
          <>
            <SectionTitle icon="cube-outline" color={RD.ink} text="Equipment & crew" />

            {/* Collapsible Helpful Hints lightbulb card */}
            <View style={s.guideCard}>
              <TouchableOpacity style={s.guideHead} onPress={() => setGearGuideOpen(v => !v)} activeOpacity={0.8}>
                <View style={s.guideHeadIcon}><Ionicons name="bulb" size={17} color={RD.info} /></View>
                <View style={{ flex: 1 }}>
                  <Text style={s.guideTitle}>Helpful Hints</Text>
                  <Text style={s.guideSub}>Specs, precautions, and how to move it</Text>
                </View>
                <Ionicons name={gearGuideOpen ? 'chevron-up' : 'chevron-down'} size={18} color={RD.textSec} />
              </TouchableOpacity>

              {gearGuideOpen && (
                <View style={s.guideBody}>
                  {(guide.specs || []).filter(Boolean).length > 0 && (
                    <GuideBlock icon="construct-outline" color={RD.info} title="Specs"
                      items={(guide.specs || []).filter(Boolean)} bullet />
                  )}
                  {(guide.precautions || []).filter(Boolean).length > 0 && (
                    <GuideBlock icon="warning-outline" color={RD.amber} title="Precautions"
                      items={(guide.precautions || []).filter(Boolean)} warn />
                  )}
                  {(guide.steps || []).filter(Boolean).length > 0 && (
                    <View style={{ marginTop: 12 }}>
                      <View style={s.guideBlockHead}>
                        <Ionicons name="list-outline" size={15} color={RD.info} />
                        <Text style={s.guideBlockTitle}>How to move it</Text>
                      </View>
                      {(guide.steps || []).filter(Boolean).map((st, i) => (
                        <View key={i} style={[s.guideStepRow, i > 0 && { marginTop: 10 }]}>
                          <View style={s.guideStepNum}><Text style={s.guideStepNumText}>{i + 1}</Text></View>
                          <Text style={s.guideStepText}>{st}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                  {toArr(guide.videos).length > 0 && (
                    <View style={{ marginTop: 12 }}>
                      <View style={s.guideBlockHead}>
                        <Ionicons name="play-circle-outline" size={15} color={RD.danger} />
                        <Text style={s.guideBlockTitle}>How-to videos</Text>
                      </View>
                      {toArr(guide.videos).map((url, i) => (
                        <VideoRow key={i} accent={RD.danger} label={`How-to video ${toArr(guide.videos).length > 1 ? i + 1 : ''}`.trim()} onPress={() => Linking.openURL(url)} />
                      ))}
                    </View>
                  )}
                </View>
              )}
            </View>

            <Text style={s.gearSubhead}>Confirm your gear</Text>

            {equipment.length === 0 && (
              <View style={s.card}><Text style={s.stepHint}>No special equipment is required for this job.</Text></View>
            )}

            {equipment.map((e) => {
              const ans = equipAnswers[e.key];
              const photo = equipPhotos[e.key];
              const wantsConfirm = e.input === 'confirm' || e.input === 'confirm_photo';
              const wantsPhoto = e.input === 'photo' || e.input === 'confirm_photo';
              const wantsYesno = e.input === 'yesno';
              return (
                <View key={e.key} style={s.equipCard}>
                  <View style={s.equipHead}>
                    <Text style={s.equipLabel}>{e.label}</Text>
                    <View style={e.required ? s.requiredBadge : s.optionalBadge}>
                      <Text style={e.required ? s.requiredText : s.optionalText}>{e.required ? 'Required' : 'Optional'}</Text>
                    </View>
                  </View>
                  {!!e.sub && <Text style={s.equipSub}>{e.sub}</Text>}
                  {!!e.spec && (
                    <View style={s.specBlock}>
                      <View style={s.specBlockHead}>
                        <Ionicons name="information-circle-outline" size={14} color={RD.info} />
                        <Text style={s.specBlockTitle}>What you need</Text>
                      </View>
                      <Text style={s.specBlockText}>{e.spec}</Text>
                    </View>
                  )}

                  {wantsYesno && (
                    <View style={s.yesnoRow}>
                      <TouchableOpacity
                        style={[s.yesnoBtn, ans === 'yes' && s.yesBtnOn]}
                        onPress={() => setEquipAnswers(p => ({ ...p, [e.key]: 'yes' }))}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="checkmark-circle" size={16} color={ans === 'yes' ? '#fff' : RD.money} />
                        <Text style={[s.yesnoText, ans === 'yes' && s.yesnoTextOn]}>Yes</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[s.yesnoBtn, ans === 'no' && s.noBtnOn]}
                        onPress={() => setEquipAnswers(p => ({ ...p, [e.key]: 'no' }))}
                        activeOpacity={0.8}
                      >
                        <Ionicons name="close-circle" size={16} color={ans === 'no' ? '#fff' : RD.danger} />
                        <Text style={[s.yesnoText, ans === 'no' && s.yesnoTextOn]}>No</Text>
                      </TouchableOpacity>
                    </View>
                  )}

                  {wantsConfirm && (
                    <TouchableOpacity
                      style={[s.confirmBtn, ans === 'confirmed' && s.confirmBtnOn]}
                      onPress={() => setEquipAnswers(p => ({ ...p, [e.key]: p[e.key] === 'confirmed' ? '' : 'confirmed' }))}
                      activeOpacity={0.8}
                    >
                      <View style={[s.checkBox, ans === 'confirmed' && s.checkBoxOn]}>
                        {ans === 'confirmed' && <Ionicons name="checkmark" size={15} color="#fff" />}
                      </View>
                      <Text style={s.confirmText}>I have this</Text>
                    </TouchableOpacity>
                  )}

                  {wantsPhoto && (
                    <>
                      <TouchableOpacity style={s.photoBtn} onPress={() => capturePhoto(uri => setEquipPhotos(p => ({ ...p, [e.key]: uri })))} activeOpacity={0.8}>
                        <Ionicons name="camera-outline" size={18} color={RD.textSec} />
                        <Text style={s.photoBtnText}>{photo ? 'Retake photo' : 'Take photo'}</Text>
                      </TouchableOpacity>
                      {!!photo && (
                        <View style={s.photoPreviewWrap}>
                          <TouchableOpacity onPress={() => setLightbox({ list: [photo], index: 0 })} activeOpacity={0.85}>
                            <Image source={{ uri: photo }} style={s.photoPreview} />
                          </TouchableOpacity>
                          <TouchableOpacity style={s.photoRemove} onPress={() => setEquipPhotos(p => { const n = { ...p }; delete n[e.key]; return n; })}>
                            <Ionicons name="close" size={12} color="#fff" />
                          </TouchableOpacity>
                        </View>
                      )}
                    </>
                  )}
                </View>
              );
            })}
          </>
        )}

        {/* ────────────────────── STEP 5 — VEHICLE & SELF ─────────────────── */}
        {step === 4 && (
          <>
            <SectionTitle icon="car-outline" color={RD.ink} text="Vehicle & self" />

            <View style={s.equipCard}>
              <View style={s.equipHead}>
                <Text style={s.equipLabel}>Empty cargo area photo</Text>
                <View style={s.requiredBadge}><Text style={s.requiredText}>Required</Text></View>
              </View>
              <Text style={s.equipSub}>Show your cargo area is clean and empty before loading.</Text>
              <TouchableOpacity style={s.photoBtn} onPress={() => capturePhoto(setCargoPhoto)} activeOpacity={0.8}>
                <Ionicons name="camera-outline" size={18} color={RD.textSec} />
                <Text style={s.photoBtnText}>{cargoPhoto ? 'Retake photo' : 'Take photo'}</Text>
              </TouchableOpacity>
              {!!cargoPhoto && (
                <View style={s.photoPreviewWrap}>
                  <TouchableOpacity onPress={() => setLightbox({ list: [cargoPhoto], index: 0 })} activeOpacity={0.85}>
                    <Image source={{ uri: cargoPhoto }} style={s.photoPreview} />
                  </TouchableOpacity>
                  <TouchableOpacity style={s.photoRemove} onPress={() => setCargoPhoto(null)}>
                    <Ionicons name="close" size={12} color="#fff" />
                  </TouchableOpacity>
                </View>
              )}
            </View>

            <YesNoCard
              label="Do you have enough fuel for the full route?"
              required value={fuelOk} onYes={() => setFuelOk('yes')} onNo={() => setFuelOk('no')}
            />
            <YesNoCard
              label="Are you rested, sober, and fit to drive?"
              required value={fitToDrive} onYes={() => setFitToDrive('yes')} onNo={() => setFitToDrive('no')}
            />
          </>
        )}

        {/* ─────────────────────────── STEP 6 — CONFIRM ───────────────────── */}
        {step === 5 && (
          <>
            <SectionTitle icon="flag-outline" color={RD.ink} text="Ready to roll" />
            <View style={s.card}>
              <SummaryRow label="Brief reviewed" ok={briefConfirmed} />
              <SummaryRow label="Order of operations" ok={opsComplete} />
              <SummaryRow label="Rules agreed" ok={rulesComplete} />
              <SummaryRow label="Equipment & crew" ok={gearRequiredOk} answered={gearAnswered} />
              <SummaryRow label="Vehicle & self" ok={vehicleRequiredOk} answered={vehicleAnswered} last />
            </View>
            {!allRequiredPassed && (
              <View style={s.warnLine}>
                <Ionicons name="warning-outline" size={15} color={RD.danger} />
                <Text style={s.warnText}>Something required is missing or marked No. You can still notify the team.</Text>
              </View>
            )}
          </>
        )}
      </ScrollView>

      {/* Bottom bar — Continue / Start route.
          🚨 MEASURED, NOT ASSUMED. The scroll area used to reserve a fixed 140px for this
          bar, but the bar is absolutely positioned and GROWS: the "Still needed" list, up
          to six bullets, and the "I can't do this" button all sit inside it. On a
          pre-dispatch brief with several path videos that put the confirm checkbox - the
          last element on the step - permanently underneath it, so the driver was told to
          confirm the brief and could not reach the control that does it. The worse the
          blockage, the taller the bar, the more it hid: it was least usable exactly when
          it mattered most. */}
      <View style={s.bottomBar} onLayout={(e) => setBarHeight(e.nativeEvent.layout.height)}>
        {step < STEPS.length - 1 && !stepReady(step) && missingForStep(step).length > 0 && (
          <View style={{ paddingHorizontal: 4, paddingBottom: 10 }}>
            <Text style={{ fontSize: 12.5, fontWeight: '700', color: RD.danger, marginBottom: 4 }}>
              Still needed before you can continue:
            </Text>
            {missingForStep(step).slice(0, 6).map((m, i) => (
              <Text key={i} style={{ fontSize: 12.5, color: RD.textSec, lineHeight: 18 }}>{`\u2022 ${m}`}</Text>
            ))}
          </View>
        )}
        {step < STEPS.length - 1 ? (
          <>
            <TouchableOpacity
              style={[s.primaryBtn, !stepReady(step) && s.btnDisabled]}
              onPress={goNext}
              disabled={!stepReady(step)}
              activeOpacity={0.85}
            >
              <Text style={s.primaryBtnText}>Continue</Text>
              <Ionicons name="arrow-forward" size={18} color="#fff" />
            </TouchableOpacity>
            {/* The way out of a step this driver cannot finish. Deliberately plain and
                secondary: it is not the normal path, and taking it flags the run. But it
                exists on EVERY gated step now, because the sheet that tells the team about
                a problem used to live behind three more steps the driver could not reach. */}
            {!stepReady(step) && (
              <TouchableOpacity
                style={s.cantDoBtn}
                onPress={() => {
                  const missing = missingForStep(step);
                  Alert.alert(
                    'Tell the team and carry on?',
                    'We will flag this job so someone picks it up with you before the run. You can keep going and finish the rest of the check-in.',
                    [
                      { text: 'Keep trying', style: 'cancel' },
                      {
                        text: 'Tell the team',
                        onPress: () => {
                          setSkipped(p => ({ ...p, [step]: missing.length ? missing : ['Could not complete this step'] }));
                          setStep(st => Math.min(st + 1, STEPS.length - 1));
                        },
                      },
                    ],
                  );
                }}
                activeOpacity={0.85}
              >
                <Ionicons name="help-buoy-outline" size={16} color={RD.danger} />
                <Text style={s.cantDoText}>I can't do this — tell the team and carry on</Text>
              </TouchableOpacity>
            )}
          </>
        ) : (
          <TouchableOpacity
            style={[s.primaryBtn, submitting && s.btnDisabled]}
            onPress={onStartRoutePress}
            disabled={submitting}
            activeOpacity={0.85}
          >
            {submitting ? <ActivityIndicator color="#fff" /> : (
              <>
                <Ionicons name="navigate" size={18} color="#fff" />
                <Text style={s.primaryBtnText}>Start route</Text>
              </>
            )}
          </TouchableOpacity>
        )}
      </View>

      {/* FAB stack — support (top) + lightbulb (bottom, pulsing) */}
      <View style={s.fabStack} pointerEvents="box-none">
        <TouchableOpacity style={[s.fab, s.fabSupport]} onPress={() => setShowSupport(true)} activeOpacity={0.85}>
          <Ionicons name="chatbubble-ellipses-outline" size={20} color="#fff" />
        </TouchableOpacity>
        <Animated.View style={[s.fab, s.fabBulb, { opacity: bulbPulse }]}>
          <TouchableOpacity style={s.fabInner} onPress={() => { setHintsOpen(true); markJobBriefViewed(jobId || passedJob?.id); }} activeOpacity={0.85}>
            <Ionicons name="bulb-outline" size={20} color="#fff" />
          </TouchableOpacity>
        </Animated.View>
      </View>

      {/* Lightbox — swipeable, rotateable, zoomable */}
      <ImageLightbox images={lightbox?.list || []} index={lightbox ? lightbox.index : null} onClose={() => setLightbox(null)} />

      {/* Helpful Hints sheet (lightbulb FAB) */}
      <Modal visible={hintsOpen} transparent animationType="slide" onRequestClose={() => setHintsOpen(false)}>
        <View style={s.sheetOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => setHintsOpen(false)} />
          <View style={s.sheet}>
            <View style={s.sheetHead}>
              <View style={s.sheetHeadIcon}><Ionicons name="bulb" size={18} color={RD.info} /></View>
              <View style={{ flex: 1 }}>
                <Text style={s.sheetTitle}>Helpful Hints</Text>
                <Text style={s.sheetSub}>Everything for this job</Text>
              </View>
              <TouchableOpacity onPress={() => setHintsOpen(false)} style={s.sheetClose} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <Ionicons name="close" size={18} color={RD.textSec} />
              </TouchableOpacity>
            </View>
            <ScrollView style={{ flex: 1 }} contentContainerStyle={s.sheetBody} showsVerticalScrollIndicator={false}>
              {/* This item first (components/LightbulbBody), then this screen's guide. */}
              <WalkInCard jobId={jobId || passedJob?.id} />
              {gallery.length > 0 && (
                <View style={s.sheetSection}>
                  <View style={s.guideBlockHead}><Ionicons name="images-outline" size={15} color={RD.maroon} /><Text style={s.guideBlockTitle}>Photos</Text></View>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginHorizontal: -4 }}>
                    {gallery.map((uri, i) => (
                      <TouchableOpacity key={i} onPress={() => setLightbox({ list: gallery, index: i })} activeOpacity={0.85}>
                        <Image source={{ uri }} style={s.galleryThumb} resizeMode="cover" />
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              )}
              {(guide.specs || []).filter(Boolean).length > 0 && (
                <View style={s.sheetSection}><GuideBlock icon="construct-outline" color={RD.info} title="Specs" items={(guide.specs || []).filter(Boolean)} bullet /></View>
              )}
              {(guide.precautions || []).filter(Boolean).length > 0 && (
                <View style={s.sheetSection}><GuideBlock icon="warning-outline" color={RD.amber} title="Precautions" items={(guide.precautions || []).filter(Boolean)} warn /></View>
              )}
              {(guide.steps || []).filter(Boolean).length > 0 && (
                <View style={s.sheetSection}>
                  <View style={s.guideBlockHead}><Ionicons name="list-outline" size={15} color={RD.info} /><Text style={s.guideBlockTitle}>How to move it</Text></View>
                  {(guide.steps || []).filter(Boolean).map((st, i) => (
                    <View key={i} style={[s.guideStepRow, i > 0 && { marginTop: 10 }]}>
                      <View style={s.guideStepNum}><Text style={s.guideStepNumText}>{i + 1}</Text></View>
                      <Text style={s.guideStepText}>{st}</Text>
                    </View>
                  ))}
                </View>
              )}
              {(toArr(guide.videos).length > 0 || hasWalkOrPath) && (
                <View style={s.sheetSection}>
                  <View style={s.guideBlockHead}><Ionicons name="play-circle-outline" size={15} color={RD.danger} /><Text style={s.guideBlockTitle}>Videos</Text></View>
                  {walkthrough.map((m, i) => <VideoRow key={`hw${i}`} accent={RD.maroon} label={`Seller walkthrough${walkthrough.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />)}
                  {sellerPath.map((m, i) => <VideoRow key={`hs${i}`} accent={RD.maroon} label={`Seller path${sellerPath.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />)}
                  {buyerPath.map((m, i) => <VideoRow key={`hb${i}`} accent={RD.info} label={`Buyer path${buyerPath.length > 1 ? ` ${i + 1}` : ''}`} onPress={() => Linking.openURL(m.url)} />)}
                  {toArr(guide.videos).map((url, i) => <VideoRow key={`hv${i}`} accent={RD.danger} label={`How-to video ${toArr(guide.videos).length > 1 ? i + 1 : ''}`.trim()} onPress={() => Linking.openURL(url)} />)}
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Support sheet (chat FAB) */}
      <Modal visible={showSupport} transparent animationType="slide" onRequestClose={() => setShowSupport(false)}>
        <View style={s.sheetOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => setShowSupport(false)} />
          <View style={s.supportSheet}>
            <View style={s.sheetHandle} />
            <View style={s.supportIconWrap}><Ionicons name="chatbubbles-outline" size={26} color={RD.maroon} /></View>
            <Text style={s.supportTitle}>Need a hand?</Text>
            <Text style={s.supportDesc}>Message the team and we will get right back to you.</Text>
            <TouchableOpacity style={s.supportBtn} onPress={() => { setShowSupport(false); navigation.navigate('SupportInbox'); }} activeOpacity={0.85}>
              <Ionicons name="chatbubble-ellipses" size={18} color="#fff" />
              <Text style={s.supportBtnText}>Open support chat</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => setShowSupport(false)} style={{ alignSelf: 'center', padding: 12 }}>
              <Text style={s.supportCancel}>Not now</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Heads-up sheet — flags the issue to the team, but the driver is never blocked */}
      <Modal visible={showBlocked} transparent animationType="slide" onRequestClose={() => setShowBlocked(false)}>
        <View style={s.sheetOverlay}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => { if (!submitting) setShowBlocked(false); }} />
          <View style={s.blockedSheet}>
            <View style={s.sheetHandle} />
            <View style={[s.blockedIconWrap, { backgroundColor: '#FFF4E5' }]}><Ionicons name="alert-circle" size={28} color="#A9760F" /></View>
            <Text style={s.blockedTitle}>Heads up before you go</Text>
            <Text style={s.blockedDesc}>
              {blockedReason || 'Something required is missing or marked No.'} We'll let the team know so they can help — you can still head out and start the pickup.
            </Text>
            <TouchableOpacity style={[s.blockedBtn, submitting && s.btnDisabled]} onPress={notifyAndContinue} disabled={submitting} activeOpacity={0.85}>
              {submitting ? <ActivityIndicator color="#fff" /> : (
                <>
                  <Ionicons name="arrow-forward-circle-outline" size={18} color="#fff" />
                  <Text style={s.blockedBtnText}>Let the team know &amp; continue</Text>
                </>
              )}
            </TouchableOpacity>
            <TouchableOpacity onPress={() => { if (!submitting) setShowBlocked(false); }} style={{ alignSelf: 'center', padding: 12 }}>
              <Text style={s.supportCancel}>Go back and fix it</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ── Small presentational helpers ─────────────────────────────────────────────
function SectionTitle({ icon, color, text }: { icon: any; color: string; text: string }) {
  return (
    <View style={s.sectionTitleRow}>
      <Ionicons name={icon} size={18} color={color} />
      <Text style={s.sectionTitleText}>{text}</Text>
    </View>
  );
}

function SpecChip({ icon, label }: { icon: any; label: string }) {
  return (
    <View style={s.specChip}>
      <Ionicons name={icon} size={13} color={RD.textSec} />
      <Text style={s.specChipText}>{label}</Text>
    </View>
  );
}

function VideoRow({ accent, label, onPress }: { accent: string; label: string; onPress: () => void }) {
  return (
    <TouchableOpacity style={s.videoRow} onPress={onPress} activeOpacity={0.7}>
      <Ionicons name="play-circle" size={26} color={accent} />
      <Text style={s.videoRowText}>{label}</Text>
      <Ionicons name="open-outline" size={15} color={RD.g30} />
    </TouchableOpacity>
  );
}

function TapConfirm({ checked, onToggle, label }: { checked: boolean; onToggle: () => void; label: string }) {
  return (
    <TouchableOpacity style={[s.tapConfirm, checked && s.tapConfirmOn]} onPress={onToggle} activeOpacity={0.8}>
      <View style={[s.checkBox, checked && s.checkBoxOn]}>
        {checked && <Ionicons name="checkmark" size={15} color="#fff" />}
      </View>
      <Text style={s.tapConfirmText}>{label}</Text>
    </TouchableOpacity>
  );
}

function YesNoCard({ label, required, value, onYes, onNo }: { label: string; required?: boolean; value?: string; onYes: () => void; onNo: () => void }) {
  return (
    <View style={s.equipCard}>
      <View style={s.equipHead}>
        <Text style={s.equipLabel}>{label}</Text>
        {required && <View style={s.requiredBadge}><Text style={s.requiredText}>Required</Text></View>}
      </View>
      <View style={s.yesnoRow}>
        <TouchableOpacity style={[s.yesnoBtn, value === 'yes' && s.yesBtnOn]} onPress={onYes} activeOpacity={0.8}>
          <Ionicons name="checkmark-circle" size={16} color={value === 'yes' ? '#fff' : RD.money} />
          <Text style={[s.yesnoText, value === 'yes' && s.yesnoTextOn]}>Yes</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[s.yesnoBtn, value === 'no' && s.noBtnOn]} onPress={onNo} activeOpacity={0.8}>
          <Ionicons name="close-circle" size={16} color={value === 'no' ? '#fff' : RD.danger} />
          <Text style={[s.yesnoText, value === 'no' && s.yesnoTextOn]}>No</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

function GuideBlock({ icon, color, title, items, bullet, warn }: { icon: any; color: string; title: string; items: string[]; bullet?: boolean; warn?: boolean }) {
  return (
    <View>
      <View style={s.guideBlockHead}>
        <Ionicons name={icon} size={15} color={color} />
        <Text style={s.guideBlockTitle}>{title}</Text>
      </View>
      <View style={[s.guideBlockCard, warn && { backgroundColor: RD.amberSoft, borderColor: RD.amberSoft }]}>
        {items.map((it, i) => (
          <View key={i} style={[s.guideItemRow, i > 0 && { marginTop: 9 }]}>
            <Ionicons name={warn ? 'alert-circle' : 'ellipse'} size={warn ? 13 : 6} color={color} style={{ marginTop: warn ? 1 : 7 }} />
            <Text style={s.guideItemText}>{it}</Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function SummaryRow({ label, ok, answered, last }: { label: string; ok: boolean; answered?: boolean; last?: boolean }) {
  // If a step is answered but has a No/missing, show danger; if unanswered, show neutral.
  const state = ok ? 'ok' : (answered === false ? 'pending' : 'fail');
  const icon = state === 'ok' ? 'checkmark-circle' : state === 'pending' ? 'ellipse-outline' : 'close-circle';
  const color = state === 'ok' ? RD.money : state === 'pending' ? RD.g30 : RD.danger;
  return (
    <View style={[s.summaryRow, !last && s.rowBorder]}>
      <Ionicons name={icon as any} size={18} color={color} />
      <Text style={s.summaryLabel}>{label}</Text>
    </View>
  );
}

const card = {
  backgroundColor: RD.surface,
  borderRadius: 18,
  borderWidth: 1,
  borderColor: RD.border,
  padding: 16,
  shadowColor: '#000',
  shadowOpacity: 0.03,
  shadowRadius: 8,
  elevation: 1,
} as const;

const s = StyleSheet.create({
  container:        { flex: 1, backgroundColor: RD.bg },
  loadingWrap:      { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: RD.bg, gap: 14, padding: 24 },
  errTitle:         { fontSize: 15, fontWeight: '600', color: RD.textSec, textAlign: 'center' },
  errBtn:           { backgroundColor: RD.ink, borderRadius: 14, paddingHorizontal: 22, paddingVertical: 12 },
  errBtnText:       { color: '#fff', fontWeight: '700', fontSize: 14 },

  // Header
  header:           { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 56 : 36, paddingBottom: spacing.md, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  backBtn:          { padding: 4, minWidth: 44, minHeight: 44, justifyContent: 'center', alignItems: 'center' },
  headerCenter:     { flex: 1, alignItems: 'center' },
  headerTitle:      { fontSize: 16, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  headerSub:        { fontSize: 12, color: RD.textFaint, marginTop: 2 },

  // Stepper
  stepperWrap:      { flexDirection: 'row', gap: 6, paddingHorizontal: spacing.base, paddingTop: 12, paddingBottom: 8, backgroundColor: RD.surface },
  stepperCol:       { flex: 1, alignItems: 'center', gap: 5 },
  stepperSeg:       { height: 4, borderRadius: 2, backgroundColor: RD.g10, alignSelf: 'stretch' },
  stepperSegDone:   { backgroundColor: RD.ink },
  stepperSegActive: { backgroundColor: RD.maroon },
  stepperLabel:     { fontSize: 9.5, fontWeight: '600', color: RD.textFaint },
  stepperLabelActive:{ color: RD.maroon, fontWeight: '700' },
  stepperLabelDone: { color: RD.ink },

  // Scroll progress
  progressBar:      { height: 3, backgroundColor: RD.g07 },
  progressFill:     { height: 3, backgroundColor: RD.money, borderRadius: 2 },

  // paddingBottom is applied inline from the measured bottom-bar height, never here.
  scroll:           { padding: spacing.base, gap: 14 },

  // Section title
  sectionTitleRow:  { flexDirection: 'row', alignItems: 'center', gap: 8 },
  sectionTitleText: { fontSize: 16, fontWeight: '700', color: RD.text, fontFamily: fonts.semiBold },
  stepHint:         { fontSize: 13, color: RD.textFaint, lineHeight: 19, marginTop: -4 },

  card:             { ...card },
  cardLabel:        { fontSize: 11, fontWeight: '700', color: RD.textFaint, letterSpacing: 0.4, marginBottom: 10, textTransform: 'uppercase' },

  // Brief
  briefItem:        { fontSize: 19, fontWeight: '700', color: RD.text },
  briefMetaRow:     { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 10 },
  catPill:          { backgroundColor: RD.maroonSoft, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5 },
  catPillText:      { color: RD.maroon, fontSize: 11.5, fontWeight: '700', textTransform: 'uppercase' },
  classPill:        { backgroundColor: RD.infoSoft, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5 },
  classPillText:    { color: RD.info, fontSize: 11.5, fontWeight: '700', textTransform: 'uppercase' },
  crewPill:         { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: RD.g03, borderRadius: 999, paddingHorizontal: 11, paddingVertical: 5 },
  crewPillText:     { color: RD.textSec, fontSize: 11.5, fontWeight: '600' },
  specRow:          { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  specChip:         { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: RD.g03, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7 },
  specChipText:     { fontSize: 12.5, fontWeight: '600', color: RD.textSec },

  // Gallery
  galleryThumb:     { width: 116, height: 116, borderRadius: radii.md, marginHorizontal: 4, backgroundColor: RD.g05 },
  galleryZoomHint:  { position: 'absolute', bottom: 6, right: 10, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 10, padding: 3 },

  // Heads up (amber list)
  headsUpCard:      { backgroundColor: RD.amberSoft, borderRadius: 18, borderWidth: 1, borderColor: RD.amberSoft, padding: 14 },
  headsUpHead:      { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 9 },
  headsUpTitle:     { fontSize: 13.5, fontWeight: '800', color: RD.amber },
  headsUpRow:       { flexDirection: 'row', gap: 9, alignItems: 'flex-start' },
  headsUpText:      { flex: 1, fontSize: 13.5, color: RD.textSec, lineHeight: 19 },

  // Install line
  installLine:      { flexDirection: 'row', alignItems: 'flex-start', gap: 10, backgroundColor: RD.infoSoft, borderRadius: 18, borderWidth: 1, borderColor: RD.info + '25', padding: 14 },
  installText:      { flex: 1, fontSize: 13, color: RD.textSec, lineHeight: 19 },

  // Video row
  videoRow:         { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, backgroundColor: RD.surface, borderWidth: 1, borderColor: RD.border, borderRadius: 14, marginBottom: 8 },
  videoRowText:     { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text },

  // Tap-to-confirm
  tapConfirm:       { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 16, borderRadius: 18, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.border },
  tapConfirmOn:     { borderColor: RD.money, backgroundColor: RD.moneySoft },
  tapConfirmText:   { flex: 1, fontSize: 14.5, fontWeight: '700', color: RD.text },

  // Order of operations
  opRow:            { flexDirection: 'row', alignItems: 'center', gap: 13, paddingVertical: 14, minHeight: 48 },
  rowBorder:        { borderBottomWidth: 1, borderBottomColor: RD.g05 },
  checkCircle:      { width: 28, height: 28, borderRadius: 14, backgroundColor: RD.g05, borderWidth: 1.5, borderColor: RD.g15, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  checkCircleOn:    { backgroundColor: RD.money, borderColor: RD.money },
  opNum:            { fontSize: 12, fontWeight: '700', color: RD.textSec },
  opText:           { flex: 1, fontSize: 14, fontWeight: '600', color: RD.text, lineHeight: 19 },

  // Rules
  ruleCard:         { flexDirection: 'row', alignItems: 'flex-start', gap: 13, padding: 15, borderRadius: 18, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.border },
  ruleCardOn:       { borderColor: RD.money },
  ruleTitle:        { fontSize: 15, fontWeight: '700', color: RD.text },
  ruleDesc:         { fontSize: 12.5, color: RD.textSec, marginTop: 3, lineHeight: 18 },

  // Checkboxes
  checkBox:         { width: 26, height: 26, borderRadius: 8, backgroundColor: RD.surface, borderWidth: 1.5, borderColor: RD.g15, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  checkBoxOn:       { backgroundColor: RD.money, borderColor: RD.money },

  // Badges
  requiredBadge:    { alignSelf: 'flex-start', backgroundColor: RD.dangerSoft, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  requiredText:     { fontSize: 10, fontWeight: '700', color: RD.danger },
  optionalBadge:    { alignSelf: 'flex-start', backgroundColor: RD.g05, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  optionalText:     { fontSize: 10, fontWeight: '700', color: RD.g40 },

  // Helpful Hints (gear step collapsible)
  guideCard:        { backgroundColor: RD.surface, borderRadius: 18, borderWidth: 1, borderColor: RD.border, overflow: 'hidden' },
  guideHead:        { flexDirection: 'row', alignItems: 'center', gap: 11, padding: 14 },
  guideHeadIcon:    { width: 34, height: 34, borderRadius: 10, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  guideTitle:       { fontSize: 15, fontWeight: '700', color: RD.text },
  guideSub:         { fontSize: 11.5, color: RD.textFaint, marginTop: 1 },
  guideBody:        { paddingHorizontal: 14, paddingBottom: 16, paddingTop: 2, borderTopWidth: 1, borderTopColor: RD.g05 },
  guideBlockHead:   { flexDirection: 'row', alignItems: 'center', gap: 7, marginTop: 12, marginBottom: 8 },
  guideBlockTitle:  { fontSize: 13, fontWeight: '700', color: RD.text },
  guideBlockCard:   { backgroundColor: RD.g03, borderRadius: 12, borderWidth: 1, borderColor: RD.border, padding: 13 },
  guideItemRow:     { flexDirection: 'row', gap: 9, alignItems: 'flex-start' },
  guideItemText:    { flex: 1, fontSize: 13, color: RD.textSec, lineHeight: 19 },
  guideStepRow:     { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
  guideStepNum:     { width: 22, height: 22, borderRadius: 11, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  guideStepNumText: { fontSize: 11, fontWeight: '700', color: RD.info },
  guideStepText:    { flex: 1, fontSize: 13.5, color: RD.textSec, lineHeight: 19, paddingTop: 1 },

  gearSubhead:      { fontSize: 13, fontWeight: '700', color: RD.text, marginTop: 4 },

  // Equipment cards
  equipCard:        { ...card },
  equipHead:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  equipLabel:       { flex: 1, fontSize: 15, fontWeight: '700', color: RD.text },
  equipSub:         { fontSize: 12.5, color: RD.textSec, marginTop: 4, lineHeight: 18 },
  specBlock:        { backgroundColor: RD.infoSoft, borderRadius: 12, padding: 11, marginTop: 11 },
  specBlockHead:    { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 5 },
  specBlockTitle:   { fontSize: 11, fontWeight: '700', color: RD.info, letterSpacing: 0.3, textTransform: 'uppercase' },
  specBlockText:    { fontSize: 13, color: RD.textSec, lineHeight: 19 },

  // Yes/No
  yesnoRow:         { flexDirection: 'row', gap: 10, marginTop: 12 },
  yesnoBtn:         { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, height: 48, borderRadius: 14, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface },
  yesBtnOn:         { backgroundColor: RD.money, borderColor: RD.money },
  noBtnOn:          { backgroundColor: RD.danger, borderColor: RD.danger },
  yesnoText:        { fontSize: 14.5, fontWeight: '700', color: RD.textSec },
  yesnoTextOn:      { color: '#fff' },

  // Confirm control
  confirmBtn:       { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 12, padding: 13, borderRadius: 14, borderWidth: 1.5, borderColor: RD.borderStrong, backgroundColor: RD.surface },
  confirmBtnOn:     { borderColor: RD.money, backgroundColor: RD.moneySoft },
  confirmText:      { fontSize: 14.5, fontWeight: '700', color: RD.text },

  // Photo capture
  photoBtn:         { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: RD.g05, borderRadius: radii.md, borderWidth: 1, borderColor: RD.borderStrong, height: 50, marginTop: 12 },
  photoBtnText:     { color: RD.textSec, fontWeight: '600', fontSize: 14 },
  photoPreviewWrap: { marginTop: 12, alignSelf: 'flex-start', position: 'relative' },
  photoPreview:     { width: 96, height: 96, borderRadius: 14, backgroundColor: RD.g05 },
  photoRemove:      { position: 'absolute', top: -7, right: -7, width: 22, height: 22, borderRadius: 11, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },

  // Summary
  summaryRow:       { flexDirection: 'row', alignItems: 'center', gap: 11, paddingVertical: 13 },
  summaryLabel:     { fontSize: 14.5, fontWeight: '600', color: RD.text },
  warnLine:         { flexDirection: 'row', alignItems: 'center', gap: 9, backgroundColor: RD.dangerSoft, borderRadius: 14, padding: 13 },
  warnText:         { flex: 1, fontSize: 13, fontWeight: '600', color: RD.danger, lineHeight: 18 },

  // Bottom bar
  cantDoBtn:        { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, marginTop: 10, paddingVertical: 11, borderRadius: 12, borderWidth: 1, borderColor: RD.border, backgroundColor: RD.surface },
  cantDoText:       { fontSize: 13.5, fontWeight: '700', color: RD.danger },
  bottomBar:        { position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: RD.surface, padding: spacing.lg, paddingBottom: Platform.OS === 'ios' ? 36 : 20, borderTopWidth: 1, borderTopColor: RD.border },
  primaryBtn:       { backgroundColor: RD.ink, borderRadius: 18, height: 60, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 9 },
  primaryBtnText:   { color: '#fff', fontWeight: '700', fontSize: 17, fontFamily: fonts.semiBold },
  btnDisabled:      { opacity: 0.45 },

  // FAB stack
  fabStack:         { position: 'absolute', right: 18, bottom: 112, alignItems: 'center', gap: 12, zIndex: 20 },
  fab:              { width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.25, shadowRadius: 8, shadowOffset: { width: 0, height: 3 }, elevation: 5 },
  fabInner:         { width: 48, height: 48, borderRadius: 24, justifyContent: 'center', alignItems: 'center' },
  fabSupport:       { backgroundColor: RD.ink },
  fabBulb:          { backgroundColor: RD.info },

  // Lightbox
  lightboxOverlay:  { flex: 1, backgroundColor: 'rgba(0,0,0,0.92)' },
  lightboxPage:     { width: SCREEN.width, height: '100%', justifyContent: 'center', alignItems: 'center' },
  lightboxImage:    { width: SCREEN.width * 0.92, height: SCREEN.height * 0.72 },
  lightboxCloseHint:{ position: 'absolute', top: Platform.OS === 'ios' ? 60 : 40, alignSelf: 'center', flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8, zIndex: 5 },
  lightboxCloseText:{ color: '#fff', fontSize: 13, fontWeight: '600' },
  lightboxCounter:  { position: 'absolute', bottom: Platform.OS === 'ios' ? 60 : 40, alignSelf: 'center', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: 20, paddingHorizontal: 14, paddingVertical: 7 },
  lightboxCounterText:{ color: '#fff', fontSize: 12, fontWeight: '600' },

  // Sheets (hints + support + blocked)
  sheetOverlay:     { flex: 1, backgroundColor: 'rgba(15,10,12,0.45)', justifyContent: 'flex-end' },
  sheet:            { backgroundColor: RD.bg, borderTopLeftRadius: 24, borderTopRightRadius: 24, maxHeight: '88%', overflow: 'hidden' },
  sheetHead:        { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 18, paddingVertical: 14, backgroundColor: RD.surface, borderBottomWidth: 1, borderBottomColor: RD.border },
  sheetHeadIcon:    { width: 34, height: 34, borderRadius: 10, backgroundColor: RD.infoSoft, justifyContent: 'center', alignItems: 'center' },
  sheetTitle:       { fontSize: 16, fontWeight: '700', color: RD.text },
  sheetSub:         { fontSize: 11.5, color: RD.textFaint, marginTop: 1 },
  sheetClose:       { width: 32, height: 32, borderRadius: 16, backgroundColor: RD.g05, justifyContent: 'center', alignItems: 'center' },
  sheetBody:        { padding: 18, paddingBottom: 44 },
  sheetSection:     { marginBottom: 16 },
  sheetHandle:      { width: 36, height: 4, borderRadius: 2, backgroundColor: RD.g15, alignSelf: 'center', marginBottom: 16 },

  // Support sheet
  supportSheet:     { backgroundColor: RD.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 40, alignItems: 'center' },
  supportIconWrap:  { width: 56, height: 56, borderRadius: 28, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center', marginBottom: 12 },
  supportTitle:     { fontSize: 19, fontWeight: '700', color: RD.text },
  supportDesc:      { fontSize: 13.5, color: RD.textSec, textAlign: 'center', marginTop: 6, marginBottom: 18, lineHeight: 20, paddingHorizontal: 8 },
  supportBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 54, alignSelf: 'stretch', borderRadius: 16, backgroundColor: RD.maroon },
  supportBtnText:   { color: '#fff', fontWeight: '700', fontSize: 15 },
  supportCancel:    { color: RD.textFaint, fontWeight: '600', fontSize: 14 },

  // Blocked sheet
  blockedSheet:     { backgroundColor: RD.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, padding: 24, paddingBottom: 40, alignItems: 'center' },
  blockedIconWrap:  { width: 56, height: 56, borderRadius: 28, backgroundColor: RD.dangerSoft, justifyContent: 'center', alignItems: 'center', marginBottom: 12 },
  blockedTitle:     { fontSize: 19, fontWeight: '700', color: RD.text },
  blockedDesc:      { fontSize: 13.5, color: RD.textSec, textAlign: 'center', marginTop: 6, marginBottom: 18, lineHeight: 20, paddingHorizontal: 4 },
  blockedBtn:       { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, height: 54, alignSelf: 'stretch', borderRadius: 16, backgroundColor: RD.danger },
  blockedBtnText:   { color: '#fff', fontWeight: '700', fontSize: 15 },
});
